import { UserInfo } from 'ui/indexPages/tables/cells';
import { Destination, DestinationStatus } from '../types/destination';

export interface GetDestinationsResponseItem {
  id: string;
  vendor: string;
  name: string;
  is_enabled: boolean;
  last_successful_transfer_ended_at: string | null;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

/**
 * Determines the status of a destination based on API response data
 *
 * @param destination - The destination data from GetDestinationsResponse
 * @param healthyTimeframeHours - Hours within which a transfer is considered "healthy" (default: 3)
 * @returns The destination status
 */
const determineDestinationStatus = (
  destination: GetDestinationsResponseItem,
  healthyTimeframeHours: number = 3,
): DestinationStatus => {
  if (!destination.is_enabled) {
    return 'Archived';
  }

  // Enabled: no successful transfers yet or had a successful transfer within the timeframe
  if (!destination.last_successful_transfer_ended_at) {
    return 'Enabled';
  }

  // Calculate time since last successful transfer
  const lastSuccessfulTransfer = new Date(destination.last_successful_transfer_ended_at);
  const now = new Date();
  const timeDifferenceMs = now.getTime() - lastSuccessfulTransfer.getTime();
  const timeDifferenceHours = timeDifferenceMs / (1000 * 60 * 60);

  // Enabled: had a successful transfer within the timeframe
  if (timeDifferenceHours <= healthyTimeframeHours) {
    return 'Enabled';
  }

  // Needs Review: enabled but last successful transfer was too long ago
  return 'Needs Review';
};

/**
 * Transforms raw API response data into a clean destination object
 *
 * @param rawDestination - The raw destination data from GetDestinationsResponse
 * @param index - index to append to the destination ID for uniqueness
 * @returns A transformed destination object with extracted fields and computed status
 */
export function transformDestinationData(
  rawDestination: GetDestinationsResponseItem,
  index: number = 0,
): Destination {
  const defaultUser: UserInfo = {
    emailAddress: 'iterable+test@iterable.com',
    fullName: 'Iterable Test',
    avatarUrl: '',
  };

  return {
    id: `${rawDestination.id}+${index}`,
    name: `[${index + 1}] ${rawDestination.name}`,
    vendor: rawDestination.vendor,
    status: determineDestinationStatus(rawDestination),
    createdAt: rawDestination.created_at,
    modifiedAt: rawDestination.updated_at,
    last_successful_transfer_ended_at: rawDestination.last_successful_transfer_ended_at,
    createdBy: defaultUser,
    modifiedBy: defaultUser,
  };
}
